import { ReactElement, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { DetailsPanel, Table, Typography } from '@weareopenr/component-library';
import { DetailedInventoryItem } from 'src/api/getInventoryItem/types';
import { formatPrice } from 'src/helpers/formatPrice/formatPrice';
import styles from './InventorySupplierDetailsTab.module.scss';

interface InventorySupplierDetailsTabProps {
  inventoryItem: DetailedInventoryItem;
}

type TSuppliedGoodRow = {
  suppliedGoodId: string;
  name: string;
  code: string;
  barcode: string;
  measurement: string;
};

type TSupplyPackageRow = {
  supplyPackageId: string;
  name: string;
  code: string;
  unitsPerPackage: number;
  carbonFootprint: string;
  cost: string | null;
  supplierName: string;
};

const suppliedGoodColumnHelper = createColumnHelper<TSuppliedGoodRow>();

const suppliedGoodColumns = [
  suppliedGoodColumnHelper.accessor('name', { header: 'Name' }),
  suppliedGoodColumnHelper.accessor('code', { header: 'Code', size: 200 }),
  suppliedGoodColumnHelper.accessor('barcode', { header: 'Barcode', size: 200 }),
  suppliedGoodColumnHelper.accessor('measurement', { header: 'Measurement', size: 160 }),
];

const supplyPackageColumnHelper = createColumnHelper<TSupplyPackageRow>();

const supplyPackageColumns = [
  supplyPackageColumnHelper.accessor('supplierName', { header: 'Supplier' }),
  supplyPackageColumnHelper.accessor('name', { header: 'Product' }),
  supplyPackageColumnHelper.accessor('code', { header: 'Code', size: 200 }),
  supplyPackageColumnHelper.accessor('unitsPerPackage', { header: 'Units/Package', size: 160 }),
  supplyPackageColumnHelper.accessor('cost', { header: 'Cost', size: 160 }),
  supplyPackageColumnHelper.accessor('carbonFootprint', { header: 'Carbon Footprint', size: 160 }),
];

const unitOnlyValues = new Set(['single', 'each']);

const formatMeasurement = (value: number | null, unit: string | null): string => {
  if (unit && unitOnlyValues.has(unit)) return unit;
  if (value === null) return '-';
  return unit ? `${value} ${unit}` : `${value}`;
};

const SuppliedGoodsEmptyState = () => (
  <div className={styles['table-empty-state']}>
    <Typography colour="primary">No supplied goods found</Typography>
  </div>
);

const InventorySupplierDetailsTab = ({ inventoryItem }: InventorySupplierDetailsTabProps): ReactElement => {
  const suppliedGoodRows = useMemo<TSuppliedGoodRow[]>(
    () =>
      inventoryItem.supplied_goods?.map((good) => ({
        suppliedGoodId: good.supplied_good_id,
        name: good.name || '-',
        code: good.code || '-',
        barcode: good.barcode || '-',
        measurement: formatMeasurement(good.measurement_value, good.measurement_unit),
      })) || [],
    [inventoryItem],
  );

  const supplyPackageRows = useMemo<TSupplyPackageRow[]>(
    () =>
      inventoryItem.supplied_goods?.flatMap((good) =>
        good.supply_packages.map((pkg) => ({
          supplyPackageId: pkg.supply_package_id,
          name: pkg.name || '-',
          code: pkg.code || '-',
          unitsPerPackage: pkg.units_per_package,
          carbonFootprint: pkg.carbon_footprint === null ? '-' : `${pkg.carbon_footprint}`,
          supplierName: pkg.supplier?.name || '-',
          cost: formatPrice(pkg.cost),
        })),
      ) || [],
    [inventoryItem],
  );

  return (
    <div className={styles['overview-container']}>
      <DetailsPanel title="Supplied Goods">
        <DetailsPanel.Row>
          <DetailsPanel.Column>
            <div className={styles['table-container']}>
              <Table data={suppliedGoodRows} columns={suppliedGoodColumns} EmptyState={SuppliedGoodsEmptyState} />
            </div>
          </DetailsPanel.Column>
        </DetailsPanel.Row>
      </DetailsPanel>
      {supplyPackageRows.length > 0 && (
        <DetailsPanel title="Supply Packages">
          <div className={styles['table-container']}>
            <Table data={supplyPackageRows} columns={supplyPackageColumns} />
          </div>
        </DetailsPanel>
      )}
    </div>
  );
};

export default InventorySupplierDetailsTab;
